import { StandardEvents } from '@shopify/events';
import { Component } from '@theme/component';

/**
 * @typedef {Object} YunaAvisosRefs
 * @property {HTMLElement} row
 *
 * @typedef {Object} AvisoEntry
 * @property {string} id
 * @property {string} titulo
 * @property {string} texto
 * @property {string} cor_txt
 * @property {string} [cor_bg]
 * @property {string} [icone_url]
 * @property {boolean} bordas
 */

/**
 * Disclosure-pill row below the ATC button. Pre-merged per-variant lists
 * are embedded server-side as a JSON blob (see blocks/avisos.liquid); the
 * client just looks up the new variant's pre-merged list when the variant
 * changes and rebuilds the row with a 200ms fade + max-height collapse.
 *
 * The motion override is intentional and DECISIONS.md-tracked — this is
 * the second sanctioned exception to the no-variant-swap-motion rule
 * (the first being yuna-discount-badge's grid-rows collapse).
 *
 * @extends {Component<YunaAvisosRefs>}
 */
class YunaAvisos extends Component {
  /** @type {string[]} */
  #currentIds = [];
  /** @type {object|null} */
  #payload = null;
  /** @type {Element | null} Where the product select listener is attached. */
  #section = null;

  connectedCallback() {
    super.connectedCallback();
    this.#cacheCurrentIds();
    this.#payload = this.#readPayload();
    // Horizon 4.1.5 announces a variant change with Shopify's standard product
    // select event; Horizon 3's variant:update, which this row used to wait
    // for, is no longer dispatched. The section is kept so the listener can be
    // removed after this element is detached, when closest() finds nothing.
    this.#section = this.closest('.shopify-section, dialog');
    this.#section?.addEventListener(StandardEvents.productSelect, this.handleProductSelect);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#section?.removeEventListener(StandardEvents.productSelect, this.handleProductSelect);
    this.#section = null;
  }

  #cacheCurrentIds() {
    const row = this.refs.row;
    if (!row) return;
    this.#currentIds = Array.from(row.querySelectorAll('[data-aviso-id]')).map(
      (el) => el.dataset.avisoId
    );
  }

  #readPayload() {
    const script = this.querySelector('script[data-yuna-avisos-payload]');
    if (!script) return null;
    try {
      return JSON.parse(script.textContent);
    } catch (e) {
      return null;
    }
  }

  /**
   * The selected variant is on the event immediately, so the row swaps on
   * click; the event's promise covers selections that only resolve to a variant
   * once the section fetch returns.
   * @param {Event} event - Shopify's standard product select event
   */
  handleProductSelect = (event) => {
    const owner = event.target instanceof Element ? event.target.closest('[data-product-id]') : null;
    const productId = owner?.getAttribute('data-product-id');
    const { detail, promise } = /** @type {{ detail?: any, promise?: Promise<any> }} */ (/** @type {unknown} */ (event));

    if (detail?.variantId) {
      this.showVariant(productId, detail.variantId);
      return;
    }
    promise
      ?.then((result) => this.showVariant(result?.detail?.productId ?? productId, result?.detail?.resource?.id))
      .catch(() => {});
  };

  /**
   * @param {string | null | undefined} productId
   * @param {string | number | null | undefined} variantId
   */
  showVariant = (productId, variantId) => {
    if (!variantId || !productId) return;

    // Combined listings can mount more than one block on a page; only react
    // to updates for our own product.
    if (String(productId) !== String(this.dataset.productId)) return;

    const payload = this.#payload;
    if (!payload || !payload.variants) return;
    const newList = payload.variants[variantId];
    if (!Array.isArray(newList)) return;

    const newIds = newList.map((a) => a.id);
    if (this.#arraysEqual(newIds, this.#currentIds)) return;

    this.#runTransition(newList, newIds);
  };

  #arraysEqual(a, b) {
    if (a.length !== b.length) return false;
    for (let i = 0; i < a.length; i++) if (a[i] !== b[i]) return false;
    return true;
  }

  #runTransition(newList, newIds) {
    const row = this.refs.row;
    if (!row) {
      this.#currentIds = newIds;
      return;
    }

    let resolved = false;
    const finish = () => {
      if (resolved) return;
      resolved = true;
      row.removeEventListener('transitionend', onTransitionEnd);
      this.#rebuildRow(newList);
      this.#currentIds = newIds;
      // double rAF so the rebuilt content lays out before we transition back in
      requestAnimationFrame(() =>
        requestAnimationFrame(() => this.classList.remove('yuna-avisos--swapping'))
      );
    };

    const onTransitionEnd = (e) => {
      // Only fire on our own row's transitions (not bubbled child transitions)
      if (e.target !== row) return;
      finish();
    };

    row.addEventListener('transitionend', onTransitionEnd);
    // Safety net: if no transition fires (reduced-motion, missing CSS, etc.)
    setTimeout(finish, 250);

    this.classList.add('yuna-avisos--swapping');
  }

  #rebuildRow(newList) {
    const row = this.refs.row;
    if (!row) return;
    row.replaceChildren();
    for (let i = 0; i < newList.length; i++) {
      row.append(this.#createPill(newList[i], i));
    }
  }

  /**
   * @param {AvisoEntry} aviso
   * @param {number} index
   */
  #createPill(aviso, index) {
    const blockId = this.dataset.blockId;
    const popoverId = `yuna-aviso-popover-${blockId}-${index}`;
    const srId = `yuna-aviso-sr-${blockId}-${index}`;
    const anchorName = `--yuna-aviso-anchor-${blockId}-${index}`;

    const bg = aviso.cor_bg && aviso.cor_bg.length > 0 ? aviso.cor_bg : 'transparent';
    const pillStyle = `--yuna-aviso-color: ${aviso.cor_txt}; --yuna-aviso-bg: ${bg}; anchor-name: ${anchorName};`;
    const texto = (aviso.texto || '').trim();
    const hasTexto = texto.length > 0;

    const buildPillInner = (host) => {
      if (aviso.icone_url) {
        const iconWrap = document.createElement('span');
        iconWrap.className = 'yuna-aviso__icon';
        iconWrap.setAttribute('aria-hidden', 'true');
        const img = document.createElement('img');
        img.src = aviso.icone_url;
        img.alt = '';
        img.width = 12;
        img.height = 12;
        img.loading = 'lazy';
        iconWrap.appendChild(img);
        host.appendChild(iconWrap);
      }
      const label = document.createElement('span');
      label.className = 'yuna-aviso__label';
      label.textContent = aviso.titulo;
      host.appendChild(label);
    };

    const pillClasses =
      'yuna-aviso__pill button-unstyled' + (aviso.bordas ? ' yuna-aviso__pill--bordered' : '');

    if (!hasTexto) {
      // Static pill: no popover, no aria-describedby, no cursor-help.
      const wrapper = document.createElement('span');
      wrapper.className = 'yuna-aviso yuna-aviso--static';
      wrapper.dataset.avisoId = aviso.id;

      const pill = document.createElement('span');
      pill.className = `${pillClasses} yuna-aviso__pill--static`;
      pill.style.cssText = pillStyle;
      buildPillInner(pill);

      wrapper.appendChild(pill);
      return wrapper;
    }

    const wrapper = document.createElement('anchored-popover-component');
    wrapper.setAttribute('data-hover-triggered', 'true');
    wrapper.className = 'yuna-aviso';
    wrapper.dataset.avisoId = aviso.id;

    const button = document.createElement('button');
    button.type = 'button';
    button.setAttribute('ref', 'trigger');
    button.className = pillClasses;
    button.setAttribute('popovertarget', popoverId);
    button.setAttribute('aria-describedby', srId);
    button.style.cssText = pillStyle;
    buildPillInner(button);

    const srText = document.createElement('span');
    srText.id = srId;
    srText.className = 'yuna-aviso__sr-text';
    srText.textContent = texto;

    const popover = document.createElement('div');
    popover.id = popoverId;
    popover.setAttribute('ref', 'popover');
    popover.className = 'yuna-aviso__popover';
    popover.setAttribute('popover', 'auto');
    popover.setAttribute('role', 'tooltip');
    popover.style.cssText = `position-anchor: ${anchorName};`;
    popover.textContent = texto;

    wrapper.appendChild(button);
    wrapper.appendChild(srText);
    wrapper.appendChild(popover);

    return wrapper;
  }
}

if (!customElements.get('yuna-avisos')) {
  customElements.define('yuna-avisos', YunaAvisos);
}
